//Solar Report - v2
//Site Details
//Site Slicer - vwSite - SiteName
//Array Details
//Site Slicer - vwSite - SiteName
//Array Slicer - vwEndUseNames - EndUseName
//Inverters
//Site Slicer - vwSite - SiteName
//Array Slicer - vwEndUseNames - Level1
//Inverter Slicer - vwEndUseNames - EndUseName
//
//Date slicers are intentionally not handled here.

import * as powerbi from 'powerbi-client';
import constants from '../constants';

// Keys of the single state object the embed keeps as source of truth.
export const STATE_KEYS = {
  SITE: 'site',
  ARRAY_DETAILS_ARRAY: 'arrayDetailsArray',
  INVERTERS_ARRAY: 'invertersArray',
  INVERTER: 'inverter',
};

// Pages are matched by `page.name`. Slicers are matched by `visual.name` and
// are listed in the order they must be applied (site, then array, then inverter).
export const SOLAR_V2_CONFIG = {
  pages: {
    siteDetails: {
      name: 'ReportSectionbcfc516900138994b861',
      slicers: [
        {
          visual: '2a5b6a20cf9dacdd1138',
          stateKey: STATE_KEYS.SITE,
          table: 'vwSite',
          column: 'SiteName',
        },
      ],
    },
    arrayDetails: {
      name: 'ea6694015e6435006491',
      slicers: [
        {
          visual: 'e517f8ad3c3756c979f5',
          stateKey: STATE_KEYS.SITE,
          table: 'vwSite',
          column: 'SiteName',
        },
        {
          visual: '8085cfe4a3e7d0c4d30e',
          stateKey: STATE_KEYS.ARRAY_DETAILS_ARRAY,
          table: 'vwEndUseNames',
          column: 'EndUseName',
        },
      ],
    },
    inverters: {
      name: 'f4e6a394a4bc03a21800',
      slicers: [
        {
          visual: 'f917bf1721e9fc38ee50',
          stateKey: STATE_KEYS.SITE,
          table: 'vwSite',
          column: 'SiteName',
        },
        {
          visual: 'c336866fc62024e49b94',
          stateKey: STATE_KEYS.INVERTERS_ARRAY,
          table: 'vwEndUseNames',
          column: 'Level1',
        },
        {
          visual: '00206259cc9035735050',
          stateKey: STATE_KEYS.INVERTER,
          table: 'vwEndUseNames',
          column: 'EndUseName',
        },
      ],
    },
  },
};

/**
 * Gets the slicer configs of a page, or an empty list for unknown pages.
 * @param {String} pageName - The page `name` (not displayName).
 * @returns {Array}
 */
export const getPageSlicers = (pageName) => {
  const page = Object.values(SOLAR_V2_CONFIG.pages).find(
    (item) => item.name === pageName
  );

  return page ? page.slicers : [];
};

const findSlicerVisual = (visuals, slicerConfig) =>
  visuals.find(
    (visual) =>
      visual.type === 'slicer' && visual.name === slicerConfig.visual
  );

/**
 * Reads the single value currently selected in a slicer visual. Anything other
 * than a Basic `In` selection (e.g. a saved NOT IN) counts as no value.
 * @param {Object} visual - The slicer visual.
 * @returns {Promise<String|null>}
 */
const readSlicerValue = async (visual) => {
  const state = await visual.getSlicerState();
  const filter = state?.filters?.[0];

  if (!filter || filter.operator !== 'In') return null;

  return filter.values?.[0] ?? null;
};

/**
 * Reads the current value of the slicer with the given state key from a page's visuals.
 * @param {Array} visuals - The page visuals.
 * @param {Object} slicerConfig - The slicer config.
 * @returns {Promise<String|null>}
 */
export const getSlicerValue = async (visuals, slicerConfig) => {
  const visual = findSlicerVisual(visuals, slicerConfig);

  if (!visual) return null;

  return readSlicerValue(visual);
};

/**
 * Removes every filter from a slicer. Skips the call when it has none.
 * @param {Object} visual - The slicer visual.
 * @returns {Promise<void>}
 */
const clearSlicer = async (visual) => {
  const state = await visual.getSlicerState();

  if (!state?.filters?.length) return;

  await visual.setSlicerState({ filters: [] });
};

/**
 * Always overwrites a slicer with a single-select Basic `In` filter, or clears
 * it when there is no value. Never merges with the saved state. Skips the call
 * when the slicer already shows the value, which keeps re-applying cheap and harmless.
 * @param {Array} visuals - The page visuals.
 * @param {Object} slicerConfig - The slicer config.
 * @param {String|null} value - The value to select, or null to clear the slicer.
 * @returns {Promise<void>}
 */
const setSlicerValue = async (visuals, slicerConfig, value) => {
  const visual = findSlicerVisual(visuals, slicerConfig);

  if (!visual) {
    console.warn(`Solar v2: slicer ${slicerConfig.visual} not found on page`);
    return;
  }

  if (!value) {
    await clearSlicer(visual);
    return;
  }

  if ((await readSlicerValue(visual)) === value) return;

  await visual.setSlicerState({
    filters: [
      {
        $schema: constants.BASIC_FILTER,
        target: { table: slicerConfig.table, column: slicerConfig.column },
        operator: 'In',
        values: [value],
        requireSingleSelection: true,
        filterType: powerbi.models.FilterType.Basic,
      },
    ],
  });
};

/**
 * Applies the stored state to the slicers of a page. A null value clears its
 * slicer. A failing slicer is logged and does not stop the others.
 * @param {String} pageName - The page `name`.
 * @param {Object} state - The stored { site, arrayDetailsArray, invertersArray, inverter }.
 * @param {Array} visuals - The page visuals.
 * @returns {Promise<void>}
 */
export const applyStateToPage = async (pageName, state, visuals) => {
  for (const slicerConfig of getPageSlicers(pageName)) {
    const value = state[slicerConfig.stateKey];

    try {
      await setSlicerValue(visuals, slicerConfig, value);
    } catch (error) {
      console.error(`Solar v2: failed to set ${slicerConfig.visual}`, error);
    }
  }
};

export default {
  applyStateToPage,
  getPageSlicers,
  getSlicerValue,
};
